import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, switchMap, startWith } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AuthService } from '../components/services/auth.service';
import { BookingService } from '../components/services/booking.service';
import { environment } from '../environments/environment';

interface Service {
  id: number;
  title: string;
  category: string;
  price: number;
  rating: number;
  provider: string;
  description: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-browse-services',
  templateUrl: './browse-services.component.html',
  styleUrls: ['./browse-services.component.css']
})
export class BrowseServicesComponent implements OnInit {
  services: Service[] = [];
  filteredServices: Service[] = [];
  allServices: Service[] = [];

  searchControl = new FormControl('');
  categoryControl = new FormControl('');
  priceFilter = 'all';
  minRating = 0;
  sortBy = 'relevance';
  page = 1;
  pageSize = 9;
  totalPages = 1;
  loading = true;

  // Booking modal state
  showBookingModal = false;
  selectedService: Service | null = null;
  bookingForm: FormGroup;
  isSubmitting = false;
  bookingSuccess = false;
  bookingError: string | null = null;

  // Booking wizard state
  bookingStep = 1;
  totalBookingSteps = 3;
  selectedUrgency = 'custom';
  urgencyOptions = [
    { value: 'asap', label: 'As soon as possible' },
    { value: 'week', label: 'Within a week' },
    { value: 'custom', label: 'Pick a date' }
  ];
  recurringOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' }
  ];

  private searchSubject = new Subject<string>();

  categories = [
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'roofing', label: 'Roofing' },
    { value: 'hvac', label: 'HVAC' },
    { value: 'framing', label: 'Framing' },
    { value: 'drywall', label: 'Drywall' },
    { value: 'painting', label: 'Painting' },
    { value: 'flooring', label: 'Flooring' },
    { value: 'general', label: 'General Contractor' }
  ];

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private authService: AuthService,
    private bookingService: BookingService,
    private router: Router
  ) {
    this.bookingForm = this.fb.group({
      date: ['', Validators.required],
      time: ['', Validators.required],
      address: ['', Validators.required],
      notes: [''],
      isRecurring: [false],
      recurringFrequency: ['']
    });
  }

  ngOnInit() {
    this.loadServices();

    // Pre-fill address from logged-in user
    const user = this.authService.getCurrentUser();
    if (user) {
      this.bookingForm.patchValue({ address: (user as any).address || '' });
    }

    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(300), distinctUntilChanged())
      .subscribe(term => this.searchSubject.next(term || ''));

    this.searchSubject
      .pipe(switchMap(term => this.searchServices(term)))
      .subscribe(results => {
        this.allServices = results;
        this.applyFilters();
      });
  }

  loadServices() {
    this.loading = true;
    this.http.get<Service[]>(environment.apiUrl + 'Services')
      .subscribe({
        next: (data) => {
          this.allServices = this.withImage(data);
          this.applyFilters();
          this.loading = false;
        },
        error: () => {
          this.allServices = this.withImage(this.getMockConstructionServices());
          this.applyFilters();
          this.loading = false;
        }
      });
  }

  private withImage(data: Service[]): Service[] {
    return data.map(s => ({
      ...s,
      imageUrl: s.imageUrl || this.pickImageForService(s.title, s.category),
      description: s.description || 'No description provided.',
      provider: s.provider || (s as any).providerName || 'Unknown Provider'
    }));
  }

  // Keywords found in the title take priority over the category, since titles
  // are more specific (e.g. a "general" job titled "Kitchen Remodel" should
  // get a kitchen photo, not a generic construction one).
  private titleKeywordTags: { keywords: string[]; tag: string }[] = [
    { keywords: ['plumb', 'pipe', 'leak', 'drain', 'water heater', 'faucet'], tag: 'plumber,pipe' },
    { keywords: ['electric', 'wiring', 'panel', 'outlet', 'lighting'], tag: 'electrician,wiring' },
    { keywords: ['roof', 'shingle'], tag: 'roofing,roof' },
    { keywords: ['hvac', 'furnace', 'duct', 'thermostat', 'air condition'], tag: 'hvac,airconditioner' },
    { keywords: ['kitchen'], tag: 'kitchenremodel,renovation' },
    { keywords: ['bathroom'], tag: 'bathroomremodel,renovation' },
    { keywords: ['frame', 'framing', 'truss'], tag: 'houseframing,carpentry' },
    { keywords: ['drywall'], tag: 'drywall,construction' },
    { keywords: ['paint'], tag: 'housepainting,paintroller' },
    { keywords: ['floor', 'hardwood', 'tile'], tag: 'hardwoodfloor,flooring' },
    { keywords: ['general contractor', 'remodel', 'renovation'], tag: 'construction,contractor' }
  ];

  private categoryTags: { [key: string]: string } = {
    plumbing: 'plumber,pipe',
    electrical: 'electrician,wiring',
    roofing: 'roofing,roof',
    hvac: 'hvac,airconditioner',
    framing: 'houseframing,carpentry',
    drywall: 'drywall,construction',
    painting: 'housepainting,paintroller',
    flooring: 'hardwoodfloor,flooring',
    general: 'construction,contractor'
  };

  // Picks a real photo from the internet that matches the service, based on
  // keywords in the title (falling back to category). LoremFlickr needs no
  // API key and returns actual Flickr photos for a given tag; `lock` pins the
  // result so the same service always gets the same photo instead of a new
  // random one on every reload.
  private pickImageForService(title: string, category: string): string {
    const t = (title || '').toLowerCase();
    const match = this.titleKeywordTags.find(m => m.keywords.some(k => t.includes(k)));
    const tag = match?.tag || this.categoryTags[category] || 'construction,contractor';
    const lock = this.hashTitle(title);
    return `https://loremflickr.com/400/300/${tag}/all?lock=${lock}`;
  }

  private hashTitle(title: string): number {
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
      hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
    }
    return hash % 1000;
  }

  searchServices(term: string) {
    const base = environment.apiUrl + 'Services';
    if (!term.trim()) return this.http.get<Service[]>(base);
    return this.http.get<Service[]>(`${base}?search=${encodeURIComponent(term)}`);
  }

  applyFilters() {
    let filtered = [...this.allServices];
    const term = this.searchControl.value?.toLowerCase() || '';
    if (term) {
      filtered = filtered.filter(s =>
        s.title.toLowerCase().includes(term) ||
        s.description.toLowerCase().includes(term) ||
        s.provider.toLowerCase().includes(term)
      );
    }
    if (this.categoryControl.value) {
      filtered = filtered.filter(s => s.category === this.categoryControl.value);
    }
    if (this.priceFilter === 'under200')    filtered = filtered.filter(s => s.price < 200);
    else if (this.priceFilter === '200to1000') filtered = filtered.filter(s => s.price >= 200 && s.price <= 1000);
    else if (this.priceFilter === 'over1000') filtered = filtered.filter(s => s.price > 1000);
    if (this.minRating > 0) filtered = filtered.filter(s => s.rating >= this.minRating);

    this.services = filtered;
    this.sortServices();
  }

  sortServices() {
    const data = [...this.services];
    if (this.sortBy === 'price-low')  data.sort((a, b) => a.price - b.price);
    else if (this.sortBy === 'price-high') data.sort((a, b) => b.price - a.price);
    else if (this.sortBy === 'rating') data.sort((a, b) => b.rating - a.rating);
    this.services = data;
    this.updatePagination();
  }

  updatePagination() {
    const start = (this.page - 1) * this.pageSize;
    this.filteredServices = this.services.slice(start, start + this.pageSize);
    this.totalPages = Math.ceil(this.services.length / this.pageSize);
  }

  changePage(p: number) {
    this.page = p;
    this.updatePagination();
    window.scrollTo(0, 0);
  }

  resetFilters() {
    this.searchControl.setValue('');
    this.categoryControl.setValue('');
    this.priceFilter = 'all';
    this.minRating = 0;
    this.sortBy = 'relevance';
    this.page = 1;
    this.loadServices();
  }

  // ── Booking ──────────────────────────────────────────────

  bookService(svc: Service, event: Event) {
    event.stopPropagation();
    this.bookingError = null;
    this.bookingSuccess = false;

    const user = this.authService.getCurrentUser();
    if (!user) {
      this.router.navigate(['/auth']);
      return;
    }
    if ((user as any).role === 'provider') {
      this.bookingError = 'Providers cannot book services. Log in as a customer to book.';
      return;
    }

    this.selectedService = svc;
    this.bookingStep = 1;
    this.selectedUrgency = 'custom';
    this.bookingForm.reset({
      date: '',
      time: '',
      address: (user as any).address || '',
      notes: '',
      isRecurring: false,
      recurringFrequency: ''
    });
    this.showBookingModal = true;
  }

  closeModal() {
    this.showBookingModal = false;
    this.selectedService = null;
    this.bookingSuccess = false;
    this.bookingError = null;
    this.bookingStep = 1;
  }

  selectUrgency(value: string) {
    this.selectedUrgency = value;
    const today = new Date();
    if (value === 'asap') {
      this.bookingForm.patchValue({ date: this.minDate });
    } else if (value === 'week') {
      today.setDate(today.getDate() + 7);
      this.bookingForm.patchValue({ date: today.toISOString().split('T')[0] });
    }
  }

  nextStep() {
    if (this.bookingStep === 1) {
      this.bookingForm.get('date')?.markAsTouched();
      this.bookingForm.get('time')?.markAsTouched();
      if (this.bookingForm.get('date')?.invalid || this.bookingForm.get('time')?.invalid) return;
      if (this.bookingForm.value.isRecurring && !this.bookingForm.value.recurringFrequency) {
        this.bookingForm.get('recurringFrequency')?.markAsTouched();
        return;
      }
    } else if (this.bookingStep === 2) {
      this.bookingForm.get('address')?.markAsTouched();
      if (this.bookingForm.get('address')?.invalid) return;
    }
    this.bookingStep++;
  }

  prevStep() {
    if (this.bookingStep > 1) this.bookingStep--;
  }

  submitBooking() {
    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      this.bookingStep = 1;
      return;
    }

    const user = this.authService.getCurrentUser();
    const svc = this.selectedService!;
    const { date, time, address, notes, isRecurring, recurringFrequency } = this.bookingForm.value;

    const booking = {
      customerId: (user as any)?.id,
      serviceId: String(svc.id),
      serviceName: svc.title,
      providerName: svc.provider,
      scheduledDate: date,
      scheduledTime: time,
      address,
      notes,
      price: svc.price,
      status: 'pending' as const,
      isRecurring: !!isRecurring,
      recurringFrequency: isRecurring ? recurringFrequency : undefined
    };

    this.isSubmitting = true;
    this.bookingService.createBooking(booking).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.bookingSuccess = true;
      },
      error: (err) => {
        this.isSubmitting = false;
        this.bookingError = err?.error?.message || 'Could not book this service. Please try again.';
      }
    });
  }

  get minDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private getMockConstructionServices(): Service[] {
    return [
      { id: 1, title: 'New Home Framing - 2,500 sqft',         category: 'framing',    price: 18500, rating: 4.9, provider: 'BuildRite Contractors',    description: 'Complete wood framing for new construction. Includes walls, floors, roof trusses.' },
      { id: 2, title: 'Roof Replacement - Architectural Shingles', category: 'roofing', price: 7200,  rating: 4.8, provider: 'TopShield Roofing',         description: 'Remove old roof, install ice & water shield, 30-year shingles.' },
      { id: 3, title: 'Whole-House Electrical Rough-In',        category: 'electrical', price: 9800,  rating: 4.7, provider: 'PowerPro Electric',          description: 'Full electrical wiring for new build. 200A panel, outlets, lighting, smoke detectors.' },
      { id: 4, title: 'HVAC Ductwork & System Install',          category: 'hvac',       price: 12400, rating: 5.0, provider: 'CoolHeat Solutions',         description: 'Central AC + furnace, duct design, zoning, smart thermostat.' },
      { id: 5, title: 'Drywall Hanging & Finishing',             category: 'drywall',    price: 3800,  rating: 4.6, provider: 'SmoothWall Pros',             description: 'Hang, tape, mud, sand. Level 5 finish available.' },
      { id: 6, title: 'Interior & Exterior Painting',            category: 'painting',   price: 5200,  rating: 4.8, provider: 'ColorCraft Painting',          description: '2-coat premium paint, trim, doors, ceilings. New construction package.' },
      { id: 7, title: 'Hardwood Flooring Installation',          category: 'flooring',   price: 6800,  rating: 4.9, provider: 'FloorMasters Inc',             description: 'Engineered hardwood, subfloor prep, staining, sealing.' },
      { id: 8, title: 'Emergency Pipe Burst Repair',             category: 'plumbing',   price: 380,   rating: 4.7, provider: 'RapidFlow Plumbing',           description: '24/7 emergency service. Water shutoff, pipe replacement, drywall repair.' },
      { id: 9, title: 'General Contractor - Full Build Oversight', category: 'general',  price: 45000, rating: 5.0, provider: 'Elite Home Builders',          description: 'Project management, permits, subs, timeline, quality control.' }
    ];
  }
}
